import * as React from 'react';

import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import debounce from 'lodash/debounce';

import { createReactRenderer } from 'helpers/react-renderer';

import Autocomplete from '..';
import GuiDefHiddenInput from '../GuiDefHiddenInput';
import { createCustomerType, createStringValueFromObject, searchCustomerTypes } from './utils';

export const ALL = { value: -1, label: 'All customer types', translate: true };

const loadSuggestions = (query: string, callback: (args?: any) => any) => {
  // @ts-ignore type difference in AutocompleteOption
  searchCustomerTypes(query).then((res) => callback([ALL].concat(res)));
};

const debouncedLoadSuggestions = debounce(loadSuggestions, 300);

const DiscountRuleCustomerTypes: React.FC<AutocompleteProps> = ({
  hasAll = false,
  isClearable = true,
  isAsync = true,
  isDisabled = false,
  defaultValue = [],
  placeholder = 'Select one or more customer types',
  hiddenInput,
  ...rest
}) => {
  const [values, setValues] = React.useState<AutocompleteOption[]>(
    !defaultValue?.length && hasAll ? [ALL] : defaultValue
  );
  const [query, setQuery] = React.useState('');

  const handleOnChange = (newValues: AutocompleteOption[]) => {
    const hasAllSelected = hasAll && values.some(({ value }) => value === ALL.value);
    const hasSelectedAll =
      hasAll && !hasAllSelected && newValues.some(({ value }) => value === ALL.value);

    if (!hasAllSelected && hasSelectedAll) {
      setValues([ALL]);
    } else if (hasAllSelected && newValues.length >= 2) {
      setValues(newValues.filter(({ value }) => value !== ALL.value));
    } else {
      setValues(newValues);
    }
  };

  const handleOnCreateOption = async (customerType: string) => {
    const newValue = await createCustomerType(customerType);
    if (newValue) {
      handleOnChange([newValue].concat(values));
    }
  };

  return (
    <>
      <Autocomplete
        aria-label="customer-types-autocomplete-input"
        {...rest}
        value={values}
        inputValue={query.trimStart()}
        loadOptions={debouncedLoadSuggestions}
        defaultOptions
        placeholder={!isDisabled && placeholder}
        isMulti
        openMenuOnFocus
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isClearable={!isDisabled ? isClearable : false}
        isDisabled={isDisabled}
        isAsync={isAsync}
        onChange={handleOnChange}
        onCreateOption={handleOnCreateOption}
        onInputChange={(query: string) => setQuery(query)}
        components={{
          DropdownIndicator: () => null,
        }}
      />

      {hiddenInput ? (
        // @ts-ignore
        <GuiDefHiddenInput {...hiddenInput} value={createStringValueFromObject(values)} />
      ) : null}
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: DiscountRuleCustomerTypes,
  selector: '[data-react-root="DiscountRuleCustomerTypes"]',
});

window.history.onChangeState(
  {
    id: undefined,
    name: 'discount_rule.views.discount_rule',
  },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default DiscountRuleCustomerTypes;
